import { ColumnDefinition } from "vmd-types";

import {
  fetchTableConfiguration,
  saveTableConfiguration,
} from "./fetch-table-configuration";

type TableConfigurationState = {
  isEditMode: boolean;
  tableHeaderEntries: ColumnDefinition[];
  confirmationPopupType?: "save" | "abort";
  isDirty?: boolean;
};

type AppLike<TState extends object> = {
  getAppState: () => TState;
  patchAppState: (patch: Partial<TState>) => void;
};

type TableConfigurationKey<TState extends object> = keyof TState & string;

type ColumnOrderValue = number[] | undefined;

const cloneHeaders = (headers: ColumnDefinition[] = []): ColumnDefinition[] =>
  headers.map((entry) => ({ ...entry }));

const readTableConfig = <TState extends object>(
  state: TState,
  configKey: TableConfigurationKey<TState>
): TableConfigurationState | undefined => {
  return state[configKey] as TableConfigurationState | undefined;
};

const readColumnOrder = <TState extends object>(
  state: TState,
  columnOrderKey: TableConfigurationKey<TState>
): ColumnOrderValue => {
  const value = state[columnOrderKey];
  return Array.isArray(value) ? (value as number[]) : undefined;
};

export const beginTableConfigurationEdit = <TState extends object>(
  app: AppLike<TState>,
  configKey: TableConfigurationKey<TState>,
  headers: ColumnDefinition[]
): void => {
  app.patchAppState({
    [configKey]: {
      isEditMode: true,
      tableHeaderEntries: cloneHeaders(headers),
      isDirty: false,
      confirmationPopupType: undefined,
    },
  } as Partial<TState>);
};

export const keepTableConfigurationInEditMode = <TState extends object>(
  app: AppLike<TState>,
  configKey: TableConfigurationKey<TState>
): void => {
  const state = app.getAppState();
  const currentConfig = readTableConfig(state, configKey);

  app.patchAppState({
    [configKey]: {
      ...currentConfig,
      isEditMode: true,
      tableHeaderEntries: cloneHeaders(currentConfig?.tableHeaderEntries),
      confirmationPopupType: undefined,
    },
  } as Partial<TState>);
};

export const setTableConfigurationConfirmationPopup = <TState extends object>(
  app: AppLike<TState>,
  configKey: TableConfigurationKey<TState>,
  type: "save" | "abort"
): void => {
  const state = app.getAppState();
  const currentConfig = readTableConfig(state, configKey);

  if (!currentConfig) {
    return;
  }

  app.patchAppState({
    [configKey]: {
      ...currentConfig,
      confirmationPopupType: type,
    },
  } as Partial<TState>);
};

export const markTableConfigurationDirty = <TState extends object>(
  app: AppLike<TState>,
  configKey: TableConfigurationKey<TState>,
  nextHeaders?: ColumnDefinition[]
): void => {
  const state = app.getAppState();
  const currentConfig = readTableConfig(state, configKey);

  app.patchAppState({
    [configKey]: {
      ...currentConfig,
      tableHeaderEntries: cloneHeaders(
        nextHeaders ?? currentConfig?.tableHeaderEntries
      ),
      isEditMode: true,
      isDirty: true,
    },
  } as Partial<TState>);
};

export const markTableConfigurationDirtyFromColumnPayload = <
  TState extends object,
>(
  app: AppLike<TState>,
  configKey: TableConfigurationKey<TState>,
  payload?: unknown
): void => {
  const state = app.getAppState();
  const currentConfig = readTableConfig(state, configKey);

  const payloadHeaders = (() => {
    if (
      payload &&
      typeof payload === "object" &&
      "headers" in payload &&
      Array.isArray((payload as { headers?: unknown }).headers)
    ) {
      return (payload as { headers?: ColumnDefinition[] }).headers;
    }

    return undefined;
  })();

  markTableConfigurationDirty(
    app,
    configKey,
    payloadHeaders ?? currentConfig?.tableHeaderEntries
  );
};

export const saveTableConfigurationAndExitEditMode = async <
  TState extends object,
>(options: {
  app: AppLike<TState>;
  configKey: TableConfigurationKey<TState>;
  columnOrderKey: TableConfigurationKey<TState>;
  componentId: string;
  applyColumnOrder: <T>(headers: T[], colOrder?: number[]) => T[];
}): Promise<void> => {
  const { app, configKey, columnOrderKey, componentId, applyColumnOrder } =
    options;
  const state = app.getAppState();
  const currentConfig = readTableConfig(state, configKey);

  const orderedHeadersForSave = applyColumnOrder(
    cloneHeaders(currentConfig?.tableHeaderEntries),
    readColumnOrder(state, columnOrderKey)
  );

  await saveTableConfiguration({
    component: componentId,
    columns: orderedHeadersForSave,
  });

  app.patchAppState({
    [configKey]: {
      isEditMode: false,
      tableHeaderEntries: orderedHeadersForSave,
      isDirty: false,
      confirmationPopupType: undefined,
    },
    [columnOrderKey]: orderedHeadersForSave.map((_, index) => index),
  } as Partial<TState>);
};

export const restorePersistedTableConfiguration = async <
  TState extends object,
>(options: {
  app: AppLike<TState>;
  configKey: TableConfigurationKey<TState>;
  columnOrderKey: TableConfigurationKey<TState>;
  componentId: string;
}): Promise<void> => {
  const { app, configKey, columnOrderKey, componentId } = options;
  const savedConfig = await fetchTableConfiguration(componentId);

  app.patchAppState({
    [configKey]: {
      isEditMode: false,
      tableHeaderEntries: cloneHeaders(savedConfig?.columns),
      isDirty: false,
      confirmationPopupType: undefined,
    },
    [columnOrderKey]: undefined,
  } as Partial<TState>);
};
